import axios from "axios";
import { v4 as uuidv4 } from "uuid";

import { IConvergeUSCredentials } from "../../../../reducers/user/types";

interface IPaymentCommandResponse {
  status: string;
  responses: {
    [key: string]: string;
  };
}

export enum POSTransactionTypes {
  SALE = "SALE",
  PRE_AUTH = "PRE_AUTH"
}

export interface IPOSPaymentInput {
  transactionType: string;
  amount: number;
  bookingId: string;
  paymentId: string;
  depositPaymentId?: string;
  invoiceId?: string;
  saveDetails: boolean;
  captureLater: boolean;
}

interface IIntegrationLogs {
  status: string;
  message: string;
  [key: string]: any;
}

export interface IPOSPaymentResponse {
  started: boolean;
  paymentGatewayId?: string;
  chanId?: string;
  message: string;
}

export enum POSTransactionStatus {
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETE = "COMPLETE",
  FAILED = "FAILED"
}

export interface IPOSTransactionArgs {
  paymentGatewayId: string;
  chanId: string;
}

const CWS_ENDPOINT = "https://localhost:9790/rest/command";
const GENERIC_ERROR_MESSAGE =
  "Failed to create transaction request. Please check if CWS is running and connected to a device.";

export async function openPaymentGateway(
  convergeUSCredentials: IConvergeUSCredentials
): Promise<IPaymentCommandResponse> {
  try {
    let paymentGatewayEnvironment = "DEMO";
    let logLevel = "TRACE";
    if (process.env.REACT_APP_NODE_ENV?.toUpperCase() === "PRODUCTION") {
      paymentGatewayEnvironment = "PROD";
      logLevel = "INFO";
    }
    const openPaymentSessionCommand = {
      method: "openPaymentGateway",
      requestId: uuidv4(),
      targetType: "paymentGatewayConverge",
      version: "1.0",
      parameters: {
        logLevel,
        app: "VMM",
        email: "tech@coastr.com",
        healthCheckStaleTransactionTimeout: 0,
        healthCheckTaskTimeout: 0,
        pin: convergeUSCredentials.pin,
        userId: convergeUSCredentials.user_id,
        handleDigitalSignature: false,
        paymentGatewayEnvironment,
        merchantId: convergeUSCredentials.merchant_id,
        terminalCardholderInputMode: "UNSET",
        vendorId: process.env.REACT_APP_CONVERGE_US_VENDOR_ID || "sc900451",
        vendorAppName: "coastrkiosk",
        vendorAppVersion: "0.1",
        bmsUsername: convergeUSCredentials.bms_username,
        bmsPassword: convergeUSCredentials.bms_password,
        overrideDefaultTerminalLanguage: {
          languageInformation: {
            languageCode: "EN",
            countryCode: "US"
          }
        }
      }
    };

    let commandResponse = await axios.post(
      CWS_ENDPOINT,
      openPaymentSessionCommand
    );

    if (
      commandResponse.data.statusDetails &&
      commandResponse.data.statusDetails === "REQUEST_ACCEPTED"
    ) {
      const paymentGatewayCommandResponse =
        commandResponse.data.data.paymentGatewayCommand.openPaymentGatewayData
          .paymentGatewayId;

      sessionStorage.setItem("paymentGatewayId", paymentGatewayCommandResponse);

      return {
        status: "completed",
        responses: {
          paymentGatewayId: paymentGatewayCommandResponse
        }
      };
    }

    return {
      status: "failed",
      responses: {
        message: "Failed to open payment gateway"
      }
    };
  } catch (error) {
    console.error(error);
    return {
      status: "failed",
      responses: {
        message: "Failed to open payment gateway"
      }
    };
  }
}

export async function createPOSSale(
  paymentGatewayId: string,
  paymentInput: IPOSPaymentInput
): Promise<IPOSPaymentResponse> {
  try {
    const {
      amount,
      bookingId,
      invoiceId,
      paymentId,
      saveDetails,
      transactionType,
      depositPaymentId
    } = paymentInput;
    if (!paymentGatewayId) {
      return {
        started: false,
        message: GENERIC_ERROR_MESSAGE
      };
    }

    let paymentIds: string[] = [paymentId];
    if (depositPaymentId) {
      paymentIds.push(depositPaymentId);
    }

    const coastr_metadata = {
      bid: bookingId,
      iid: invoiceId,
      pids: paymentIds,
      sd: saveDetails
    };

    let command = {
      method: "startPaymentTransaction",
      requestId: uuidv4(),
      targetType: "paymentGatewayConverge",
      version: "1.0",
      parameters: {
        paymentGatewayId,
        note: JSON.stringify(coastr_metadata),
        transactionType,
        baseTransactionAmount: {
          value: amount,
          currencyCode: "USD"
        },
        partialApprovalAllowed: true,
        tenderType: "CARD",
        isTaxInclusive: false,
        generateToken: false,
        addToken: false
      }
    };

    // Auto Charge
    if (saveDetails) {
      command.parameters.generateToken = true;
      command.parameters.addToken = true;
    }

    let commandResponse = await axios.post(CWS_ENDPOINT, command);
    console.log(commandResponse.data)
    if (
      commandResponse.data.data.paymentGatewayCommand.chanId &&
      commandResponse.data.statusDetails === "REQUEST_ACCEPTED"
    ) {
      return {
        started: true,
        message: `
          Transaction has started. Please check the card machine and proceed with payment.

          You can safely navigate away from this page, this page will be automatically
          updated once the payment is complete.
        `,
        paymentGatewayId,
        chanId: commandResponse.data.data.paymentGatewayCommand.chanId,
      };
    } else {
      return {
        started: false,
        message: GENERIC_ERROR_MESSAGE
      };
    }
  } catch (error) {
    console.error(error);
    return {
      started: false,
      message: GENERIC_ERROR_MESSAGE
    };
  }
}

export async function createPOSPayment(
  credentials: IConvergeUSCredentials,
  args: IPOSPaymentInput
): Promise<IPOSPaymentResponse> {
  let paymentGatewayId = sessionStorage.getItem("paymentGatewayId") || "";
  if (!paymentGatewayId) {
    const openPaymentGatewayResponse = await openPaymentGateway(credentials);
    if (openPaymentGatewayResponse.status === "completed") {
      paymentGatewayId = openPaymentGatewayResponse.responses.paymentGatewayId;
    }
  }
  if (!paymentGatewayId || paymentGatewayId === "") {
    return {
      started: false,
      message: GENERIC_ERROR_MESSAGE
    };
  }
  return await createPOSSale(paymentGatewayId, args);
}

export async function testIntegration(): Promise<IIntegrationLogs[]> {
  let logs: IIntegrationLogs[] = [];
  try {
    let command = {
      method: "getEnvironmentInfo",
      requestId: uuidv4(),
      targetType: "api"
    };

    let commandResponse = await axios.post(CWS_ENDPOINT, command);

    if (commandResponse.status === 200) {
      logs.push({
        status: "info",
        message: "Able to connect to CWS"
      });
    } else {
      logs.push({
        status: "error",
        message:
          "Unable to connect to CWS, please check if CWS is running on this system."
      });
      return logs;
    }

    command.method = "getCardReaderInfo";

    commandResponse = await axios.post(CWS_ENDPOINT, command);

    if (commandResponse.status === 200 && commandResponse.data.cardReaderInfo) {
      logs.push({
        status: "info",
        message: "Detected Card Readers",
        cardReaderInfo: commandResponse.data.cardReaderInfo
      });
    } else {
      logs.push({
        status: "error",
        message:
          "Could not detect any card readers. Please check if the card reader is plugged into the system."
      });
      return logs;
    }

    return logs;
  } catch (error) {
    console.error(error);
    return logs;
  }
}

export async function getPOSPaymentStatus(
  args: IPOSTransactionArgs
): Promise<POSTransactionStatus> {
  try {
    const { paymentGatewayId, chanId } = args;
    let command = {
      method: "getPaymentTransactionStatus",
      requestId: uuidv4(),
      targetType: "paymentGatewayConverge",
      version: "1.0",
      parameters: {
        paymentGatewayId,
        chanId
      }
    };

    let commandResponse = await axios.post(CWS_ENDPOINT, command);

    const paymentCompleted: boolean =
      commandResponse.data.data.paymentGatewayCommand.completed || false;
    const paymentGatewayTransactionData =
      commandResponse.data.data.paymentGatewayCommand.paymentTransactionData;

    if (
      paymentCompleted &&
      paymentGatewayTransactionData.result === "APPROVED"
    ) {
      return POSTransactionStatus.COMPLETE;
    } else if (paymentCompleted && commandResponse.data.data.error) {
      return POSTransactionStatus.FAILED;
    } else if (paymentCompleted === false) {
      return POSTransactionStatus.IN_PROGRESS;
    }

    return POSTransactionStatus.FAILED;
  } catch (error) {
    console.error(error);
    return POSTransactionStatus.FAILED;
  }
}
